/**
 * MCPCorrectionsPage — admin surface for the pillar-6 correction taxonomy.
 *
 * Audience: super_admin (any org) and contractor_admin (own org). RLS
 * enforces the org scoping; the route gate enforces the role.
 *
 * Capabilities:
 *   - List active corrections newest-first, filterable by category
 *   - Inline category change (reclassify)
 *   - Inline deactivate (soft delete; row stays in audit trail)
 *
 * Not in scope (intentional):
 *   - Showing input_pattern / bad_output / corrected_output content. These
 *     can leak the upstream tool output that a user once flagged. The
 *     admin lookup goes via the Audit MCP server's read tools, which
 *     run through the spine and the verifier — not via this UI's free-
 *     text rendering.
 *   - Manual creation of corrections. Corrections are created by the
 *     `report_correction` MCP tool when a user flags AI output.
 */

import { useMemo, useState } from 'react'
import {
  MCP_CORRECTION_CATEGORIES,
  CATEGORY_LABEL,
  type McpCorrectionCategory,
  type MCPCorrectionRow,
} from '@/types/mcpCorrection'
import { useMCPCorrections }              from '@/hooks/useMCPCorrections'
import { useUpdateCorrectionCategory }    from '@/hooks/useUpdateCorrectionCategory'
import { useDeactivateCorrection }        from '@/hooks/useDeactivateCorrection'

export function MCPCorrectionsPage(): JSX.Element {
  const [filter, setFilter] = useState<McpCorrectionCategory | 'all'>('all')

  const list  = useMCPCorrections(filter === 'all' ? {} : { category: filter })
  const reclassify = useUpdateCorrectionCategory()
  const deactivate = useDeactivateCorrection()

  const rows    = list.data ?? []
  // Both mutation hooks throw `AppError`, so userMessage is always present.
  const errMsg  = reclassify.error?.userMessage ?? deactivate.error?.userMessage ?? null

  return (
    <div style={{ maxWidth: '64rem', margin: '0 auto', padding: '1.5rem' }}>
      <header style={{ marginBottom: '1.25rem' }}>
        <h1 style={{ fontSize: '1.4rem', margin: 0, color: 'var(--brand-neutral-900)' }}>
          MCP corrections
        </h1>
        <p style={{ marginTop: '0.4rem', color: 'var(--brand-neutral-500)', fontSize: '0.9rem' }}>
          User-flagged AI output filtered or replaced on future tool calls. Reclassify or
          deactivate as needed; the row stays in the audit trail either way.
        </p>
      </header>

      <FilterBar value={filter} onChange={setFilter} count={rows.length} />

      {errMsg && (
        <div
          role="alert"
          style={{
            background:    'var(--brand-danger-50, #fef2f2)',
            color:         'var(--brand-danger-800, #991b1b)',
            border:        '1px solid var(--brand-danger-200, #fecaca)',
            padding:       '0.6rem 0.75rem',
            borderRadius:  '0.4rem',
            marginBottom:  '0.75rem',
            fontSize:      '0.9rem',
          }}
        >
          {errMsg}
        </div>
      )}

      {list.isLoading ? (
        <p style={{ color: 'var(--brand-neutral-500)' }}>Loading corrections…</p>
      ) : rows.length === 0 ? (
        <EmptyHint filter={filter} />
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {rows.map((row) => (
            <CorrectionRow
              key={row.id}
              row={row}
              onReclassify={(c) => reclassify.mutate({ correctionId: row.id, nextCategory: c })}
              onDeactivate={() => deactivate.mutate({ correctionId: row.id })}
              busy={reclassify.isPending || deactivate.isPending}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

interface FilterBarProps {
  value:    McpCorrectionCategory | 'all'
  onChange: (next: McpCorrectionCategory | 'all') => void
  count:    number
}

function FilterBar(props: FilterBarProps): JSX.Element {
  return (
    <div
      style={{
        display:       'flex',
        alignItems:    'center',
        gap:           '0.75rem',
        marginBottom:  '0.75rem',
      }}
    >
      <label style={{ fontSize: '0.85rem', color: 'var(--brand-neutral-700)' }}>
        Filter
        <select
          aria-label="Filter by category"
          value={props.value}
          onChange={(e) => props.onChange(e.target.value as McpCorrectionCategory | 'all')}
          style={{ marginLeft: '0.4rem', padding: '0.3rem 0.5rem', borderRadius: '0.3rem' }}
        >
          <option value="all">All categories</option>
          {MCP_CORRECTION_CATEGORIES.map((c) => (
            <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>
          ))}
        </select>
      </label>
      <span style={{ marginLeft: 'auto', color: 'var(--brand-neutral-500)', fontSize: '0.85rem' }}>
        {props.count} active
      </span>
    </div>
  )
}

function EmptyHint({ filter }: { filter: McpCorrectionCategory | 'all' }): JSX.Element {
  const msg = filter === 'all'
    ? 'No active corrections in this organization yet.'
    : `No active corrections in the “${CATEGORY_LABEL[filter]}” category.`
  return (
    <p style={{ color: 'var(--brand-neutral-500)', textAlign: 'center', padding: '2rem' }}>
      {msg}
    </p>
  )
}

interface CorrectionRowProps {
  row:          MCPCorrectionRow
  onReclassify: (next: McpCorrectionCategory) => void
  onDeactivate: () => void
  busy:         boolean
}

function CorrectionRow({ row, onReclassify, onDeactivate, busy }: CorrectionRowProps): JSX.Element {
  const created = useMemo(() => new Date(row.created_at).toLocaleString(), [row.created_at])
  return (
    <li
      style={{
        background:   'var(--brand-surface, #fff)',
        border:       '1px solid var(--brand-neutral-200, #e5e7eb)',
        borderRadius: '0.5rem',
        padding:      '0.75rem',
        display:      'flex',
        flexDirection:'column',
        gap:          '0.4rem',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap' }}>
        <CategoryBadge category={row.category} />
        <strong style={{ color: 'var(--brand-neutral-900)' }}>
          {row.server_name}.{row.tool_name}
        </strong>
        <span style={{ color: 'var(--brand-neutral-500)', fontSize: '0.85rem' }}>{created}</span>
      </div>

      {row.reason && (
        <p style={{ margin: 0, color: 'var(--brand-neutral-700)', fontSize: '0.9rem' }}>
          {row.reason}
        </p>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
        <label style={{ fontSize: '0.85rem' }}>
          Category
          <select
            aria-label={`Change category for ${row.id}`}
            value={row.category}
            disabled={busy}
            onChange={(e) => onReclassify(e.target.value as McpCorrectionCategory)}
            style={{ marginLeft: '0.4rem', padding: '0.25rem 0.4rem' }}
          >
            {MCP_CORRECTION_CATEGORIES.map((c) => (
              <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>
            ))}
          </select>
        </label>

        <button
          type="button"
          onClick={onDeactivate}
          disabled={busy}
          style={{
            marginLeft:    'auto',
            background:    'var(--brand-danger-50, #fef2f2)',
            color:         'var(--brand-danger-800, #991b1b)',
            border:        '1px solid var(--brand-danger-200, #fecaca)',
            padding:       '0.3rem 0.6rem',
            borderRadius:  '0.3rem',
            cursor:        busy ? 'not-allowed' : 'pointer',
            fontSize:      '0.85rem',
          }}
        >
          Deactivate
        </button>
      </div>
    </li>
  )
}

function CategoryBadge({ category }: { category: McpCorrectionCategory }): JSX.Element {
  return (
    <span
      style={{
        background:    'var(--brand-neutral-100, #f3f4f6)',
        color:         'var(--brand-neutral-700, #374151)',
        border:        '1px solid var(--brand-neutral-300, #d1d5db)',
        padding:       '0.1rem 0.45rem',
        borderRadius:  '0.25rem',
        fontSize:      '0.75rem',
        fontWeight:    600,
        letterSpacing: '0.02em',
      }}
    >
      {CATEGORY_LABEL[category]}
    </span>
  )
}
